import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

interface ApplicationReturnLinkProps {
  className?: string;
  alwaysShowSpace?: boolean;
}

export function ApplicationReturnLink({ className = "mt-6", alwaysShowSpace = false }: ApplicationReturnLinkProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [applicationName, setApplicationName] = useState<string | null>(null);
  const [applicationUrl, setApplicationUrl] = useState<string | null>(null);
  
  useEffect(() => {
    // Check for application info periodically as it may be set asynchronously
    const checkForAppInfo = () => {
      const name = sessionStorage.getItem('application_name');
      const url = sessionStorage.getItem('application_url');
      
      if (name && url) {
        setApplicationName(name);
        setApplicationUrl(url);
        setIsLoading(false);
      } else if (!alwaysShowSpace) {
        // If not showing space and no app info after initial check, hide
        setIsLoading(false);
      }
    };
    
    // Initial check
    checkForAppInfo();
    
    // Set up polling for a short period in case data arrives late
    const interval = setInterval(checkForAppInfo, 100);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setIsLoading(false);
    }, 2000); // Stop checking after 2 seconds
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [alwaysShowSpace]);
  
  // When alwaysShowSpace is true, we reserve space even while loading
  if (alwaysShowSpace && isLoading) {
    return (
      <div className={`text-center ${className}`}>
        <p className="text-sm text-muted-foreground mb-3 invisible">
          Having trouble signing in?
        </p>
        <div className="inline-flex items-center justify-center h-8 px-3 rounded-md">
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }
  
  // If no application info and not reserving space, return null
  if (!applicationUrl || !applicationName) return null;
  
  return (
    <div className={`text-center ${className}`}>
      <p className="text-sm text-muted-foreground mb-3">
        Having trouble signing in?
      </p>
      <Button
        variant="outline"
        size="sm"
        asChild
      >
        <a href={applicationUrl}>
          Return to {applicationName}
        </a>
      </Button>
    </div>
  );
}